import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ArrowRight, Check, CheckCircle2 } from 'lucide-react';
import { estimatorData, ProjectCategory, LogicCondition } from '../data/estimatorLogic';

interface ProjectEstimatorProps {
  isOpen: boolean;
  onClose: () => void;
}

const ProjectEstimator: React.FC<ProjectEstimatorProps> = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(0); // 0: project type, 1...n: questions, n+1: summary
  const [projectType, setProjectType] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [finalTier, setFinalTier] = useState<{ tier: string; range: [number, number]; hourlyRate: string; estimatedHours: string; } | null>(
    null,
  );

  // Reset state when modal closes (after animation)
  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        setStep(0);
        setProjectType(null);
        setAnswers({});
        setFinalTier(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const categoryData = projectType ? (estimatorData[projectType] as ProjectCategory) : null;
  const questions = categoryData?.questions || [];
  const isSummaryStep = projectType && step > questions.length;

  const handleSelectProjectType = (type: string) => {
    setProjectType(type);
    setStep(1);
    setAnswers({});
  };

  const handleAnswer = (questionId: string, value: string, isMulti: boolean) => {
    setAnswers((prev) => {
      if (isMulti) {
        const current = (prev[questionId] as string[]) || [];
        if (current.includes(value)) {
          return { ...prev, [questionId]: current.filter((v) => v !== value) };
        } else {
          return { ...prev, [questionId]: [...current, value] };
        }
      } else {
        return { ...prev, [questionId]: value };
      }
    });
  };

  const calculateTier = () => {
    if (!categoryData) return;

    let matchedLogic: LogicCondition | null = null;

    // Check conditions in order. The last one that matches is usually not what we want,
    // wait, we should find the FIRST matching condition from top to bottom, or maybe
    // they are mutually exclusive? Let's check them top to bottom and return the first match.
    // Actually, usually more complex overrides less complex. Let's process top to bottom and
    // keep overriding, or first match?
    // Let's look at website: pages: 1_5 + presence -> Market Based.
    // pages: 12_plus -> Complex.
    // features contains backend -> Complex.
    // Let's evaluate top to bottom, if match, set it, so the LAST match wins (which usually are the complex overrides).

    for (const logic of categoryData.logic) {
      let isMatch = true;
      for (const [key, value] of Object.entries(logic.conditions)) {
        if (key.endsWith('CountMax')) {
          const targetKey = key.replace('CountMax', '');
          const count = (answers[targetKey] as string[])?.length || 0;
          if (count > (value as number)) isMatch = false;
        } else if (key.endsWith('CountMin')) {
          const targetKey = key.replace('CountMin', '');
          const count = (answers[targetKey] as string[])?.length || 0;
          if (count < (value as number)) isMatch = false;
        } else {
          // Normal value matching
          const answerValue = answers[key];
          if (Array.isArray(value)) {
            // Condition value is array (e.g. features: ["backend", ...])
            // If user has selected ANY of these features, it's a match.
            const userArray = (answerValue as string[]) || [];
            const hasIntersection = value.some((v) => userArray.includes(v as string));
            if (!hasIntersection && value.length > 0) isMatch = false;
            // if value is empty array [], it means MUST HAVE NO features.
            if (value.length === 0 && userArray.length > 0) isMatch = false;
          } else {
            // Exact match
            if (answerValue !== value) isMatch = false;
          }
        }
      }
      if (isMatch) {
        matchedLogic = logic;
      }
    }

    // Default fallback if nothing matches perfectly
    if (!matchedLogic && categoryData.logic.length > 0) {
      matchedLogic = categoryData.logic[0];
    }

    if (matchedLogic) {
      setFinalTier({ tier: matchedLogic.tier, range: matchedLogic.range, hourlyRate: matchedLogic.hourlyRate, estimatedHours: matchedLogic.estimatedHours });
    }

    setStep(questions.length + 1);
  };

  const handleNext = () => {
    if (step === questions.length) {
      calculateTier();
    } else {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  // Progress percentage
  const progress = projectType ? (isSummaryStep ? 100 : (step / (questions.length + 1)) * 100) : 0;

  const currentQuestion =
    projectType && step > 0 && step <= questions.length ? questions[step - 1] : null;

  const isNextDisabled = currentQuestion
    ? currentQuestion.multiSelect
      ? !(answers[currentQuestion.id] as string[])?.length
      : !answers[currentQuestion.id]
    : false;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-2xl bg-zinc-950 border border-white/10 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
            style={{ minHeight: '500px', maxHeight: '90vh' }}
          >
            {/* Header & Progress */}
            <div className="px-6 py-4 border-b border-white/10 flex justify-between items-center relative z-10 bg-zinc-950/80 backdrop-blur-md">
              <div className="flex items-center gap-3">
                {step > 0 && !isSummaryStep && (
                  <button
                    onClick={handleBack}
                    className="text-stone-400 hover:text-white transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                )}
                <h3 className="text-lg font-bold text-white tracking-wide">
                  Project <span className="text-cyan-400">Estimator</span>
                </h3>
              </div>
              <button
                onClick={onClose}
                className="text-stone-400 hover:text-white transition-colors p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Progress Bar */}
            {projectType && !isSummaryStep && (
              <div className="w-full h-1 bg-zinc-800">
                <motion.div
                  className="h-full bg-gradient-to-r from-cyan-500 to-purple-500"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            )}

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-6 sm:p-8 relative">
              <AnimatePresence mode="wait">
                {/* Step 0: Project Type Selection */}
                {step === 0 && (
                  <motion.div
                    key="step-0"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="flex flex-col h-full justify-center"
                  >
                    <div className="text-center mb-8">
                      <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                        What are we building?
                      </h2>
                      <p className="text-stone-400">Select a project type to get started.</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {[
                        { id: 'website', label: 'Website', icon: '🌐', color: 'cyan' },
                        { id: 'mobile_app', label: 'Mobile App', icon: '📱', color: 'rose' },
                        { id: 'ai_agent', label: 'AI Agent', icon: '🤖', color: 'purple' },
                      ].map((type) => (
                        <button
                          key={type.id}
                          onClick={() => handleSelectProjectType(type.id)}
                          className="flex flex-col items-center justify-center p-6 bg-zinc-900 border border-white/5 rounded-xl hover:bg-zinc-800 hover:border-white/20 transition-all group"
                        >
                          <span className="text-4xl mb-3 group-hover:scale-110 transition-transform">
                            {type.icon}
                          </span>
                          <span className="text-white font-medium">{type.label}</span>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Questions Steps */}
                {step > 0 && currentQuestion && (
                  <motion.div
                    key={`step-${step}`}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="flex flex-col h-full"
                  >
                    <div className="mb-8">
                      <span className="text-xs font-semibold text-cyan-400 uppercase tracking-wider mb-2 block">
                        Question {step} of {questions.length}
                      </span>
                      <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                        {currentQuestion.question}
                      </h2>
                      {currentQuestion.multiSelect && (
                        <p className="text-stone-400 text-sm">Select all that apply.</p>
                      )}
                    </div>

                    <div className="space-y-3 flex-1">
                      {Object.entries(currentQuestion.options).map(([key, label]) => {
                        const isSelected = currentQuestion.multiSelect
                          ? ((answers[currentQuestion.id] as string[]) || []).includes(key)
                          : answers[currentQuestion.id] === key;

                        return (
                          <button
                            key={key}
                            onClick={() =>
                              handleAnswer(currentQuestion.id, key, !!currentQuestion.multiSelect)
                            }
                            className={`w-full flex items-center p-4 rounded-xl border transition-all ${
                              isSelected
                                ? 'bg-zinc-800 border-cyan-500/50 shadow-[0_0_15px_rgba(6,182,212,0.15)]'
                                : 'bg-zinc-900/50 border-white/5 hover:border-white/20 hover:bg-zinc-800/80'
                            }`}
                          >
                            <div
                              className={`w-5 h-5 rounded flex items-center justify-center mr-4 flex-shrink-0 transition-colors ${
                                currentQuestion.multiSelect ? 'rounded-md' : 'rounded-full'
                              } ${
                                isSelected ? 'bg-cyan-500 text-white' : 'border border-stone-600'
                              }`}
                            >
                              {isSelected && <Check className="w-3 h-3" strokeWidth={3} />}
                            </div>
                            <span
                              className={`text-left ${isSelected ? 'text-white' : 'text-stone-300'}`}
                            >
                              {label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                )}

                {/* Summary Step */}
                {isSummaryStep && finalTier && (
                  <motion.div
                    key="summary"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex flex-col h-full items-center justify-center text-center py-8"
                  >
                    <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-purple-500 rounded-full flex items-center justify-center mb-6">
                      <CheckCircle2 className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-white mb-2">Your Estimate</h2>
                    <p className="text-stone-400 mb-8 max-w-md">
                      Based on your selections, here is the recommended tier and estimated build
                      cost for your project.
                    </p>

                    <div className="bg-zinc-900 border border-white/10 rounded-2xl p-8 w-full max-w-sm mb-8 relative overflow-hidden group">
                      <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />

                      <span className="text-cyan-400 text-sm font-semibold tracking-wider uppercase mb-2 block">
                        Recommended Tier
                      </span>
                      <h3 className="text-2xl font-bold text-white mb-4">{finalTier.tier}</h3>

                      <div className="text-left w-full">
                        <p className="text-sm text-stone-400 mb-1 text-center">
                          {finalTier.tier === 'Starter Plan' ? 'Hourly Rate' : 'Flexible Development Pricing'}
                        </p>
                        <p className="text-2xl font-bold text-white text-center">{finalTier.hourlyRate}</p>
                        
                        <div className="mt-4 space-y-3 pt-4 border-t border-white/10 text-center">
                          <p className="text-med text-stone-400">
                            Average Estimated Hours:{' '}
                            <span className="text-stone-300">{finalTier.estimatedHours}</span>
                          </p>
                          <p className="text-sm text-stone-400">
                            Estimated Investment: <br />
                            <span className="text-3xl font-bold bg-gradient-to-r from-white to-stone-400 bg-clip-text text-transparent mt-2 block">
                              {finalTier.range[0] === finalTier.range[1]
                                ? `${formatPrice(finalTier.range[0])}+`
                                : `${formatPrice(finalTier.range[0])} - ${formatPrice(finalTier.range[1])}`}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md">
                      <button
                        onClick={onClose}
                        className="w-full sm:w-1/2 px-8 py-3 rounded-full font-medium transition-colors border border-white/20 text-white hover:bg-white/10 flex items-center justify-center"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => {
                          let answersText = '\n\nMy Answers:\n';
                          questions.forEach((q) => {
                            const ans = answers[q.id];
                            if (ans) {
                              if (Array.isArray(ans) && ans.length > 0) {
                                const labels = ans.map((a) => q.options[a]).join(', ');
                                answersText += `- ${q.question} ${labels}\n`;
                              } else if (!Array.isArray(ans)) {
                                answersText += `- ${q.question} ${q.options[ans]}\n`;
                              }
                            }
                          });

                          const msg = `Project Estimate Details:\nType: ${projectType}\nTier: ${finalTier.tier}\nEstimated Investment: ${finalTier.range[0] === finalTier.range[1] ? `${formatPrice(finalTier.range[0])}+` : `${formatPrice(finalTier.range[0])} - ${formatPrice(finalTier.range[1])}`}\nFlexible Dev Pricing: ${finalTier.hourlyRate}\nEstimated Hours: ${finalTier.estimatedHours}${answersText}`;
                          const solMap: Record<string, string> = {
                            website: 'Website',
                            mobile_app: 'Mobile App',
                            ai_agent: 'AI Agent',
                          };
                          const sol = projectType ? solMap[projectType] : '';

                          const url = new URL('/contact', window.location.origin);
                          url.searchParams.set('message', msg);
                          if (sol) url.searchParams.set('solution', sol);

                          window.location.href = url.toString();
                        }}
                        className="w-full sm:w-1/2 bg-white text-black hover:bg-stone-200 px-8 py-3 rounded-full font-medium transition-colors flex items-center justify-center gap-2"
                      >
                        Add to Quote Request
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Footer Navigation */}
            {step > 0 && !isSummaryStep && (
              <div className="p-4 sm:p-6 border-t border-white/5 bg-zinc-950 flex justify-end">
                <button
                  onClick={handleNext}
                  disabled={isNextDisabled}
                  className={`flex items-center gap-2 px-6 py-2.5 rounded-full font-medium transition-all ${
                    isNextDisabled
                      ? 'bg-zinc-800 text-stone-500 cursor-not-allowed'
                      : 'bg-white text-black hover:bg-stone-200 shadow-[0_0_20px_rgba(255,255,255,0.1)] hover:scale-105'
                  }`}
                >
                  {step === questions.length ? 'Calculate Estimate' : 'Next'}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ProjectEstimator;
